import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigation, useRoute } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Alert, StyleSheet, Text, View } from 'react-native';
import * as yup from 'yup';
import { AuthShell, IconTextField, PrimaryCTA } from '../../components';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import {
  clearError,
  resetPassword,
  selectAuthError,
  selectIsResettingPassword,
  selectValidationErrors,
  selectVerificationToken,
} from './authSlice';

const schema = yup.object({
  newPassword: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('New password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your password'),
});

interface RouteParams {
  code?: string;
  email?: string;
}

type StrengthLabel = 'Too short' | 'Weak' | 'Good' | 'Strong' | '';

const getStrength = (p: string): { label: StrengthLabel; color: string } => {
  if (p.length === 0) return { label: '', color: fpColors.outlineVariant };
  if (p.length < 8) return { label: 'Too short', color: fpColors.error };
  if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(p))
    return { label: 'Strong', color: '#22C55E' };
  if (/^(?=.*[a-zA-Z])(?=.*\d)/.test(p))
    return { label: 'Good', color: '#EAE555' };
  return { label: 'Weak', color: fpColors.error };
};

export const ResetPasswordScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useAppDispatch();

  const { code = '', email } = (route.params as RouteParams) || {};

  const isLoading = useAppSelector(selectIsResettingPassword);
  const error = useAppSelector(selectAuthError);
  const validationErrors = useAppSelector(selectValidationErrors);
  const verificationToken = useAppSelector(selectVerificationToken);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<{ newPassword: string; confirmPassword: string }>({
    resolver: yupResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const newPassword = watch('newPassword');
  const strength = getStrength(newPassword);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const onSubmit = async (data: {
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (!verificationToken) {
      Alert.alert(
        'Error',
        'No reset token found. Please request a new password reset.',
      );
      return;
    }
    const result = await dispatch(
      resetPassword({
        code,
        newPassword: data.newPassword,
        token: verificationToken,
      }),
    );
    if (resetPassword.fulfilled.match(result)) {
      (navigation as any).navigate('Login');
    }
  };

  const errorText = (() => {
    if (typeof error === 'string' && error.length > 0) return error;
    if (validationErrors) {
      if (typeof validationErrors === 'string') return validationErrors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const first = validationErrors[0];
        return typeof first === 'string' ? first : (first as any)?.message ?? null;
      }
    }
    return null;
  })();

  return (
    <AuthShell
      showBack
      onBack={() => (navigation as any).navigate('ForgotPassword')}
    >
      <View style={styles.headingBlock}>
        <View style={styles.iconBadge}>
          <MaterialIcons name="vpn-key" size={28} color={fpColors.primary} />
        </View>
        <Text style={styles.h1}>Set a new password</Text>
        <Text style={styles.subtitle}>
          {email
            ? `Create a new password for ${email}`
            : 'Create a new secure password for your account.'}
        </Text>
      </View>

      <View style={styles.form}>
        <Controller
          control={control}
          name="newPassword"
          render={({ field: { onChange, onBlur, value } }) => (
            <View>
              <IconTextField
                label="New password"
                leftIcon="lock"
                rightIcon={showPassword ? 'visibility-off' : 'visibility'}
                onRightIconPress={() => setShowPassword((v) => !v)}
                placeholder="At least 8 characters"
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={errors.newPassword?.message}
                secureTextEntry={!showPassword}
                autoCapitalize="none"
                autoComplete="new-password"
                autoFocus
              />
              {value ? (
                <View style={styles.strengthRow}>
                  <Text style={styles.strengthLabel}>Password strength: </Text>
                  <Text style={[styles.strengthValue, { color: strength.color }]}>
                    {strength.label}
                  </Text>
                </View>
              ) : null}
            </View>
          )}
        />

        <Controller
          control={control}
          name="confirmPassword"
          render={({ field: { onChange, onBlur, value } }) => (
            <IconTextField
              label="Confirm new password"
              leftIcon="lock"
              rightIcon={showConfirm ? 'visibility-off' : 'visibility'}
              onRightIconPress={() => setShowConfirm((v) => !v)}
              placeholder="Re-enter password"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              error={errors.confirmPassword?.message}
              secureTextEntry={!showConfirm}
              autoCapitalize="none"
              autoComplete="new-password"
            />
          )}
        />

        <View style={styles.requirements}>
          <Text style={styles.requirementsTitle}>Password requirements</Text>
          <RequirementRow label="At least 8 characters" met={newPassword.length >= 8} />
          <RequirementRow
            label="Mix of upper and lower case letters"
            met={/[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)}
          />
          <RequirementRow
            label="At least one number"
            met={/\d/.test(newPassword)}
          />
        </View>

        {errorText ? (
          <View style={styles.errorBanner}>
            <MaterialIcons name="error-outline" size={18} color={fpColors.error} />
            <Text style={styles.errorBannerText}>{errorText}</Text>
          </View>
        ) : null}

        <PrimaryCTA
          label="Reset password"
          onPress={handleSubmit(onSubmit)}
          loading={isLoading}
        />
      </View>
    </AuthShell>
  );
};

const RequirementRow: React.FC<{ label: string; met: boolean }> = ({
  label,
  met,
}) => (
  <View style={styles.reqRow}>
    <MaterialIcons
      name={met ? 'check-circle' : 'radio-button-unchecked'}
      size={16}
      color={met ? fpColors.primary : fpColors.outlineVariant}
    />
    <Text style={[styles.reqText, met && styles.reqTextMet]}>{label}</Text>
  </View>
);

const styles = StyleSheet.create({
  headingBlock: {
    marginBottom: 14,
    alignItems: 'center',
  },
  iconBadge: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  h1: {
    fontFamily: 'Inter_700Bold',
    fontSize: 22,
    color: fpColors.onBackground,
    marginBottom: 4,
    textAlign: 'center',
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
  form: {
    gap: 12,
  },
  strengthRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  strengthLabel: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  strengthValue: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
  },
  requirements: {
    padding: 10,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainerLow,
    gap: 4,
  },
  requirementsTitle: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onSurface,
    marginBottom: 2,
  },
  reqRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  reqText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  reqTextMet: {
    color: fpColors.onSurface,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorBannerText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onErrorContainer,
  },
});
